/**
 * Text Validation Utilities
 *
 * Reusable validation functions for text input validation
 */

/**
 * Arabic Unicode ranges:
 * - \u0600-\u06FF: Arabic (main block)
 * - \u0750-\u077F: Arabic Supplement
 * - \u08A0-\u08FF: Arabic Extended-A
 */
const ARABIC_PATTERN = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\s\.\,\-\']+$/;

/**
 * Validates if text contains only Arabic characters, spaces, and common punctuation
 *
 * @param text - The text to validate
 * @returns true if text is empty or contains only Arabic characters, false otherwise
 *
 * @example
 * isArabicOnly('دار النشر العربية') // true
 * isArabicOnly('دار النشر - الرياض') // true (allows punctuation)
 * isArabicOnly('ABC Publishing') // false
 * isArabicOnly('دار ABC النشر') // false (mixed)
 */
export const isArabicOnly = (text: string): boolean => {
  if (!text.trim()) return true; // Empty is valid (required check is separate)
  return ARABIC_PATTERN.test(text);
};

/**
 * English/Latin character pattern (including common European characters)
 */
const ENGLISH_PATTERN = /^[a-zA-Z\s\.\,\-\']+$/;

/**
 * Validates if text contains only English/Latin characters, spaces, and common punctuation
 *
 * @param text - The text to validate
 * @returns true if text is empty or contains only English characters, false otherwise
 *
 * @example
 * isEnglishOnly('ABC Publishing House') // true
 * isEnglishOnly('O\'Connor Publishing') // true (allows apostrophe)
 * isEnglishOnly('دار النشر') // false
 */
export const isEnglishOnly = (text: string): boolean => {
  if (!text.trim()) return true;
  return ENGLISH_PATTERN.test(text);
};

/**
 * Checks if text contains any Arabic characters
 *
 * @param text - The text to check
 * @returns true if text contains at least one Arabic character
 */
export const containsArabic = (text: string): boolean => {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(text);
};

/**
 * Checks if text contains any English/Latin characters
 *
 * @param text - The text to check
 * @returns true if text contains at least one English character
 */
export const containsEnglish = (text: string): boolean => {
  return /[a-zA-Z]/.test(text);
};

/**
 * Phone number pattern - must start with country code (+ or 00)
 * Format: +/00 followed by 1-4 digit country code, optional dash, then 6-14 digits
 * No spaces allowed
 */
const PHONE_WITH_COUNTRY_CODE_PATTERN = /^(00|\+)[0-9]{1,4}-?[0-9]{6,14}$/;

/**
 * Validates if phone number starts with a country code (+ or 00)
 * Spaces are not allowed in the phone number.
 *
 * @param phone - The phone number to validate
 * @returns true if phone is empty or starts with valid country code format, false otherwise
 *
 * @example
 * isValidPhoneWithCountryCode('+966501234567') // true
 * isValidPhoneWithCountryCode('00966501234567') // true
 * isValidPhoneWithCountryCode('+966-501234567') // true (dash allowed)
 * isValidPhoneWithCountryCode('+966 501234567') // false (spaces not allowed)
 * isValidPhoneWithCountryCode('0501234567') // false (no country code)
 * isValidPhoneWithCountryCode('501234567') // false (no country code)
 */
export const isValidPhoneWithCountryCode = (phone: string): boolean => {
  if (!phone.trim()) return true; // Empty is valid (required check is separate)
  return PHONE_WITH_COUNTRY_CODE_PATTERN.test(phone.trim());
};
